import { RefreshCw } from "lucide-react";
import { safeErrorMessage, isSessionGone } from "@/lib/errors";

/** One error-with-retry surface for the whole app.
 *
 *  Takes the ERROR OBJECT, not a message string. That is the point: the component is
 *  the only thing that decides what text appears, so no call site can accidentally
 *  pass the server's response body through. See lib/errors.js for why that matters.
 *
 *  `variant`:
 *    "block"  — the thing failed and there is nothing to show. Replaces the content.
 *    "inline" — stale data is still on screen and a refresh failed. Sits above it and
 *               says so, because stale panels passing for current ones is worse than
 *               an error.
 *
 *  `note` is for a consequence the user needs spelled out ("don't read an empty screen
 *  as a clear day"). It is author-written copy from the call site, never server text.
 */
export default function ErrorRetry({
  error,
  onRetry,
  title = "That didn't load",
  note,
  variant = "block",
  testid = "error-retry",
}) {
  if (!error) return null;
  const message = safeErrorMessage(error);
  // A 401 is about to become a redirect to the login screen; a retry would just 401
  // again, so offer the explanation without the dead button.
  const canRetry = Boolean(onRetry) && !isSessionGone(error);

  if (variant === "inline") {
    return (
      <div
        className="flex items-start justify-between gap-4 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2"
        role="alert"
        data-testid={testid}
      >
        <div>
          <p>{message}</p>
          {note && <p className="mt-0.5 text-destructive/90">{note}</p>}
        </div>
        {canRetry && (
          <button
            onClick={onRetry}
            data-testid={`${testid}-button`}
            className="shrink-0 font-semibold underline"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" role="alert" data-testid={testid}>
      <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-4 py-3">
        <p className="font-medium">{title}</p>
        <p className="mt-1 text-destructive/90">{message}</p>
        {note && <p className="mt-2 text-destructive/90">{note}</p>}
      </div>
      {canRetry && (
        <button
          onClick={onRetry}
          data-testid={`${testid}-button`}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-md px-4 py-2.5 text-sm font-semibold hover:bg-primary/90"
        >
          <RefreshCw className="w-4 h-4" /> Try again
        </button>
      )}
    </div>
  );
}
